<template>
    <LoginScreen>
        <template v-if="isLoginProcessSelectionVisible" #inner-container-left>
            <div
                ref="selectionContainerRef"
                tabindex="-1"
                class="mt-16 flex flex-col gap-6 w-full rounded focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-gray-700 focus-visible:ring-offset-4"
            >
                <h2 class="flex flex-col items-center">
                    <span class="font-light text-xl/none">{{ $t('login-heading-subtitle') }}</span>
                    <span class="font-poppins text-4xl font-bold">{{ $t('app-name') }}</span>
                </h2>

                <div class="flex flex-col items-stretch gap-2">
                    <div v-if="props.initialMessage" class="border-4 border-sky-700/50 rounded-sm">
                        <Message status="info">
                            {{ props.initialMessage }}
                        </Message>
                    </div>

                    <div v-if="props.initialError" class="border-4 border-red-700/50 rounded-sm">
                        <Message status="error">
                            {{ props.initialError }}
                        </Message>
                    </div>

                    <template v-if="config.authMethods.length === 0">
                        <div class="flex flex-col gap-2 items-stretch w-full border-4 border-red-700/50 rounded-sm">
                            <Message :headline="$t('login-no-methods-configured-headline')" status="error">
                                <div class="flex flex-col gap-2">
                                    <p>{{ $t('login-no-methods-configured') }}</p>
                                </div>
                            </Message>
                        </div>
                    </template>

                    <div v-if="ssoLoginError" class="border-4 border-red-700/50 rounded-sm">
                        <Message status="error">
                            {{ ssoLoginError }}
                        </Message>
                    </div>

                    <template v-for="method in config.authMethods" :key="method">
                        <button
                            v-if="method === 'sso-km'"
                            type="button"
                            class="bg-koelnmesse rounded-sm text-base leading-6 flex flex-row items-center justify-center gap-2.5 py-1.75 px-2.5 text-white text-light border border-transparent cursor-pointer text-nowrap focus-visible:outline-offset-3 focus-visible:outline-3 focus-visible:outline-koelnmesse hover:bg-[color-mix(in_oklab,var(--color-koelnmesse),black_10%)] active:bg-[color-mix(in_oklab,var(--color-koelnmesse),black_20%)]"
                            @click="submitSsoLogin('sso-km')"
                        >
                            <KoelnmesseLogo class="h-5" />
                            {{ $t('login-koelnmesse-account') }}
                        </button>

                        <button
                            v-if="method === 'sso-ldt'"
                            type="button"
                            class="bg-sky-800 rounded-sm text-base leading-6 flex flex-row items-center justify-center gap-2.5 py-1.75 px-2.5 text-white text-light border border-transparent cursor-pointer text-nowrap focus-visible:outline-offset-3 focus-visible:outline-3 focus-visible:outline-sky-800 hover:bg-sky-900 active:bg-sky-950"
                            @click="submitSsoLogin('sso-ldt')"
                        >
                            <SSOIcon class="size-5 text-sky-50" />
                            {{ $t('login-ldt-account') }}
                        </button>

                        <button
                            v-if="method === 'password'"
                            class="bg-sky-50 text-gray-700 border border-gray-700 rounded-sm text-base leading-6 flex flex-row items-center justify-center gap-2 py-1.75 pl-2 pr-2.5 text-light cursor-pointer focus-visible:outline-offset-3 focus-visible:outline-3 focus-visible:outline-gray-700 hover:bg-gray-700 hover:text-gray-50 active:bg-gray-800 active:text-gray-50 group"
                            @click="showDeveloperForm"
                        >
                            <CodeIcon
                                class="size-5 text-gray-600 group-hover:text-gray-100 group-active:text-gray-100"
                            />
                            {{ $t('login-password-account') }}
                        </button>
                    </template>
                </div>

                <section
                    v-if="config.authMethods.includes('sso-ldt') || config.authMethods.includes('sso-km')"
                    class="mt-auto space-y-1"
                >
                    <h2 class="font-semibold text-lg/none text-gray-500">{{ $t('login-access-heading') }}</h2>
                    <p v-if="config.authMethods.includes('sso-km')" class="text-gray-500 font-light text-sm">
                        {{ $t('login-sso-km-description') }}
                    </p>
                    <p v-if="config.authMethods.includes('sso-ldt')" class="text-gray-500 font-light text-sm">
                        {{ $t('login-sso-ldt-description') }}
                    </p>
                    <p v-if="config.authMethods.includes('sso-km')" class="text-gray-500 font-light text-sm">
                        {{ $t('login-sso-fallback-description') }}
                    </p>
                </section>
            </div>
        </template>

        <template v-else #inner-container-left>
            <div
                ref="passwordFormContainerRef"
                tabindex="-1"
                class="mt-16 flex flex-col gap-6 w-full rounded focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-gray-700 focus-visible:ring-offset-4"
            >
                <h2 class="flex flex-col items-center">
                    <span class="font-light text-xl/none">{{ $t('login-heading-subtitle') }}</span>
                    <span class="font-poppins text-4xl font-bold">{{ $t('app-name') }}</span>
                </h2>

                <div class="flex flex-col gap-2 items-stretch w-full">
                    <Button
                        size="Base"
                        variant="Transparent-Light"
                        :icon-description="$t('login-back-to-selection')"
                        @click="showSelectionScreen"
                    >
                        <template #icon>
                            <BackIcon class="size-5" />
                        </template>
                    </Button>

                    <div v-if="emailPasswordLoginError" class="border-4 border-red-700/50 rounded-sm">
                        <Message status="error">
                            {{ emailPasswordLoginError }}
                        </Message>
                    </div>

                    <form class="flex flex-col gap-2 items-stretch w-full" @submit.prevent="submitLogin">
                        <TextField
                            :label="$t('email')"
                            :value="email"
                            autocomplete="email"
                            @input="email = ($event.target as HTMLInputElement).value"
                        />

                        <PasswordField
                            :label="$t('password')"
                            :value="password"
                            autocomplete="current-password"
                            @input="password = ($event.target as HTMLInputElement).value"
                        />

                        <Button
                            size="Base"
                            variant="Neutral-Strong"
                            type="submit"
                            :disabled-explanation="
                                emailPasswordLogin.loading.value ? $t('login-in-progress') : undefined
                            "
                            @click="submitLogin"
                        >
                            <template #icon>
                                <LoginIcon class="size-5" />
                            </template>

                            {{ $t('login') }}
                        </Button>
                    </form>
                </div>
            </div>
        </template>

        <template #inner-container-right>
            <div class="space-y-4">
                <h1 class="font-poppins font-bold text-6xl">{{ $t('app-name') }}</h1>

                <section>
                    <h2 class="font-semibold text-gray-700 text-xl">{{ $t('login-feature-venue-heading') }}</h2>
                    <CheckList>
                        <CheckListItem>{{ $t('login-feature-venue-buildings') }}</CheckListItem>
                        <CheckListItem>{{ $t('login-feature-venue-outdoor') }}</CheckListItem>
                    </CheckList>
                </section>

                <section>
                    <h2 class="font-semibold text-gray-700 text-xl">{{ $t('events') }}</h2>
                    <CheckList>
                        <CheckListItem>{{ $t('login-feature-events-overview') }}</CheckListItem>
                        <CheckListItem>{{ $t('login-feature-events-planning') }}</CheckListItem>
                    </CheckList>
                </section>

                <section>
                    <h2 class="font-semibold text-gray-700 text-xl">{{ $t('projects') }}</h2>
                    <CheckList>
                        <CheckListItem>{{ $t('login-feature-projects-overview') }}</CheckListItem>
                        <CheckListItem>{{ $t('login-feature-projects-planning') }}</CheckListItem>
                    </CheckList>
                </section>

                <section>
                    <h2 class="font-semibold text-gray-700 text-xl">{{ $t('external-events') }}</h2>
                    <CheckList>
                        <CheckListItem>{{ $t('login-feature-external-events-overview') }}</CheckListItem>
                    </CheckList>
                </section>
            </div>
        </template>

        <template #inner-container-bottom>
            <h2 class="font-semibold text-lg text-gray-500">{{ $t('login-contact-heading') }}</h2>
            <i18n path="login-contact-description" tag="p" class="text-gray-500 font-light text-sm">
                <template #mail>
                    <span class="inline-flex items-center gap-1">
                        <MailIcon class="size-3.5" />

                        <a
                            href="mailto:space.cap@koelnmesse.de"
                            class="hover:text-gray-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-1 focus-visible:rounded-xs"
                        >
                            space.cap@koelnmesse.de
                        </a>
                    </span>
                </template>
            </i18n>
        </template>

        <template #version-indicator>
            <div v-if="versionText" class="text-sky-50/30 selection:bg-sky-900 selection:text-sky-50">
                {{ versionText }}
            </div>
        </template>
    </LoginScreen>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import Button from '@/components/Button/Button.vue';
import CheckList from '@/components/CheckList/CheckList.vue';
import CheckListItem from '@/components/CheckList/CheckListItem.vue';
import PasswordField from '@/components/Form/PasswordField.vue';
import TextField from '@/components/Form/TextField.vue';
import BackIcon from '@/components/Icon/BackIcon.vue';
import CodeIcon from '@/components/Icon/CodeIcon.vue';
import KoelnmesseLogo from '@/components/Icon/KoelnmesseLogo.vue';
import LoginIcon from '@/components/Icon/LoginIcon.vue';
import MailIcon from '@/components/Icon/MailIcon.vue';
import SSOIcon from '@/components/Icon/SSOIcon.vue';
import LoginScreen from '@/components/LoginScreen/LoginScreen.vue';
import Message from '@/components/Message/Message.vue';
import { triggerHideTooltipKey } from '@/components/Tooltip/injectionKeys';
import { config } from '@/config';
import { EntraLoginDocument, LoginDocument, SsoProvider } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { useVersionInfo } from '@/utils/hooks/useVersionInfo';
import { useHeadSafe } from '@unhead/vue';
import { useMutation } from '@vue/apollo-composable';
import { computed, nextTick, provide, ref } from 'vue';
import { useRouter } from 'vue-router';
import { EntraSsoButton, useEntraLogin } from '../composables/useEntraLogin';

type LoginPageProps = {
    redirect?: string;
    initialError?: string;
    initialMessage?: string;
};

const props = defineProps<LoginPageProps>();

useHeadSafe({ title: () => $t('login') });

const isLoginProcessSelectionVisible = ref(true);
const selectionContainerRef = ref<HTMLElement>();
const passwordFormContainerRef = ref<HTMLElement>();

const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

async function showDeveloperForm() {
    isLoginProcessSelectionVisible.value = false;
    triggerHideTooltipKeyCounter.value++;
    await nextTick();
    passwordFormContainerRef.value?.focus();
}

async function showSelectionScreen() {
    isLoginProcessSelectionVisible.value = true;
    triggerHideTooltipKeyCounter.value++;
    await nextTick();
    selectionContainerRef.value?.focus();
}

const { deploymentInfo } = useVersionInfo(config.versionJsonUrl);

const versionText = computed(() => {
    const info = deploymentInfo.value;
    if (info.status !== 'Success') {
        return null;
    }

    return $t('login-version-text', {
        hash: info.commitShortHash,
        environment: info.environmentLong,
        timestamp: info.commitTimestampLong,
    });
});

const router = useRouter();

const email = ref('');
const password = ref('');
const emailPasswordLoginError = ref<string | undefined>(undefined);

const emailPasswordLogin = useMutation(LoginDocument);

async function submitLogin(): Promise<void> {
    emailPasswordLoginError.value = undefined;

    const res = await emailPasswordLogin.mutate({ email: email.value, password: password.value });
    if (res?.data?.login?.__typename === 'Session') {
        auth.storeSession(res.data.login);
        router.replace(props.redirect ?? '/');
    } else if (res?.data?.login?.__typename === 'InvalidCredentials') {
        emailPasswordLoginError.value = $t('login-error-invalid-credentials');
    } else {
        emailPasswordLoginError.value = $t('login-error-unknown');
    }
}

const BUTTON_TO_GQL_PROVIDER: Record<EntraSsoButton, SsoProvider> = {
    'sso-km': SsoProvider.SsoKm,
    'sso-ldt': SsoProvider.SsoLdt,
};

const { loginWithEntra } = useEntraLogin();
const entraLoginMutation = useMutation(EntraLoginDocument);
const ssoLoginError = ref<string | undefined>(undefined);

async function submitSsoLogin(provider: EntraSsoButton): Promise<void> {
    ssoLoginError.value = undefined;

    let idToken: string;
    try {
        idToken = await loginWithEntra(provider);
    } catch (error) {
        ssoLoginError.value = `${$t('login-error-sso-failed')}: ${error}`;
        return;
    }

    const response = await entraLoginMutation.mutate({
        provider: BUTTON_TO_GQL_PROVIDER[provider],
        idToken,
    });
    if (response?.data?.entraLogin?.__typename === 'Session') {
        auth.storeSession(response.data.entraLogin);
        router.replace(props.redirect ?? '/');
    } else if (response?.data?.entraLogin?.__typename === 'InvalidCredentials') {
        ssoLoginError.value = $t('login-error-sso-no-account');
    } else {
        ssoLoginError.value = $t('login-error-unknown');
    }
}
</script>
